import { Component, NgZone, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { SeoService } from '../../shared/services/seo-service/seo.service';
import { DataSharingService } from '../../shared/services/data-sharing/data-sharing.service';
import { CommonService } from '../../shared/services/common/common.service';

interface Contract {
  id: string;
  name: string;
  lotSize: number;
  asset: string;
  basePrice: number;
}

interface CalcResult {
  notional: number;
  entryFee: number;
  exitFee: number;
  totalFees: number;
  totalWithGST: number;
  breakeven: number;
  netPnl: number;
}

@Component({
  selector: 'app-fees',
  templateUrl: './fees.component.html',
  styleUrl: './fees.component.css',
})
export class FeesComponent {
  activeMainTab = 'trading';
  GST = 0.18;
  notional: number = 0;
  entryFee: number = 0;
  totalFees: number = 0;
  totalWithGST: number = 0;
  mainTabs = [
    { id: 'trading', icon: 'pi pi-chart-line', label: 'Trading Fees' },
    { id: 'calculator', icon: 'pi pi-calculator', label: 'Fee Calculator' },
    { id: 'deposit', icon: 'pi pi-wallet', label: 'Deposit & Withdrawal' },
    {
      id: 'liquidation',
      icon: 'pi pi-exclamation-triangle',
      label: 'Liquidation Fees',
    },
  ];

  heroStats = [
    { value: '0.01%', label: 'Lowest Maker Fee' },
    { value: '0.01%', label: 'Lowest Taker Fee' },
    { value: '18%', label: 'GST Inclusive' },
    { value: '24/7', label: 'Trading Available' },
  ];

  // ── Calculator state ──────────────────────────────────
  calcType: 'futures' | 'options' = 'futures';
  selectedContract = 'BTCUSDT';
  entryPrice = 68500;
  exitPrice = 67500;
  quantity = 1;
  entryType: 'maker' | 'taker' = 'maker';
  exitType: 'maker' | 'taker' = 'taker';
  scalperActive = false;
  indexPrice: number = 0;
  pnl: number = 0;
  result: any = {
    notional: 0,
    entryFee: 0,
    exitFee: 0,
    totalFees: 0,
    totalWithGST: 0,
    breakeven: 0,
    netPnl: 0,
  };

  contracts: any;

  get selectedContractObj(): Contract | undefined {
    return this.contracts.find((c: any) => c.id === this.selectedContract);
  }

  // Futures maker/taker rates
  private rates = {
    futures: { maker: 0.0001, taker: 0.0001 },
    options: { maker: 0.0001, taker: 0.0001, feeCapRate: 0.0175 },
  };

  // ── Deposit / Withdrawal data ─────────────────────────
  depositFees = [
    {
      icon: 'pi pi-building', // bank icon
      name: 'INR (Bank)',
      deposit: 'Free',
      withdrawal: '₹0',
      minWithdrawal: '₹0',
    },
  ];

  // ── Liquidation data ──────────────────────────────────
  liquidationFees = [
    {
      type: 'Isolated Margin',
      fee: '0.1%',
      insurance: 'Remaining margin',
      description: 'Applied on notional value at liquidation price',
    },
    {
      type: 'Cross Margin',
      fee: '0.1%',
      insurance: 'Remaining margin',
      description:
        'Position closed using mark price; surplus to insurance fund',
    },
  ];

  constructor(
    private ngZone: NgZone,
    private dataSharingService: DataSharingService,
    private commonService: CommonService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.contracts = this.commonService.getFuturesArray();

    this.dataSharingService.futuresData$ //this.socketService.listen('futures-data')
      .subscribe((data: any) => {
        if (data.futuresData) {
          this.indexPrice = data.futuresData.filter(
            (_d: any) => _d.s == this.selectedContract,
          )[0].i;
        }
      });
    if (this.indexPrice) {
      let obj = {
        markPrice: this.entryPrice,
        exitPrice: this.exitPrice,
        contractType: this.calcType,
        size: this.quantity,
        symbol: this.selectedContract,
        indexPrice: this.indexPrice,
      };
      this.calculateTradingFee(obj);
    }
  }

  calculateTradingFee = (data: any) => {
    const { markPrice, indexPrice, contractType, size, symbol, exitPrice } =
      data;
    console.log(
      'check the options...',
      markPrice,
      indexPrice,
      contractType,
      size,
      symbol,
      exitPrice,
    );
    if (contractType === 'futures') {
      const lotUnit = this.commonService.getFuturesArrayByUnit(symbol);

      const sizeNum = Number(size);
      const lotNum = Number(lotUnit[0].lotSize);

      // Use index prices for notional calculation
      const entryIndex = Number(indexPrice);
      const exitIndex = Number(exitPrice);

      const entryNotional = sizeNum * lotNum * entryIndex;
      const exitNotional = sizeNum * lotNum * exitIndex;

      const entryFee = entryNotional * this.rates.futures.maker;
      const exitFee = exitNotional * this.rates.futures.maker;

      const finalFee = entryFee + exitFee;
      const totalFinalFee = finalFee * (1 + this.GST);
      this.notional = entryNotional + exitNotional;
      this.entryFee = entryFee;
      this.totalFees = finalFee;
      this.totalWithGST = totalFinalFee;
      let orderData = {
        tradePrice: markPrice,
        size: size,
        positionSide: 'buy',
      };
      const PNL = this.calculatePNLFutures(orderData, exitPrice, symbol);
      this.pnl = PNL - this.totalWithGST;
    } else if (contractType === 'options') {
      const lotUnit = this.commonService.getFuturesArrayByUnit(symbol);

      const sizeNum = Number(size);
      const lotNum = Number(lotUnit[0].lotSize);
      const indexNum = Number(indexPrice);
      const entryPremium = Number(markPrice);
      const exitPremium = Number(exitPrice);

      // Notional fee
      const notionalFee =
        sizeNum * lotNum * indexNum * this.rates.options.maker;

      // Cap at % of premium
      const entryCap =
        sizeNum * lotNum * entryPremium * this.rates.options.feeCapRate;
      const exitCap =
        sizeNum * lotNum * exitPremium * this.rates.options.feeCapRate;

      const entryFee = Math.min(notionalFee, entryCap);
      const exitFee = Math.min(notionalFee, exitCap);

      const finalFee = entryFee + exitFee;

      const totalFinalFee = finalFee * (1 + this.GST);

      this.notional = notionalFee;
      this.entryFee = entryFee;
      this.totalFees = finalFee;
      this.totalWithGST = totalFinalFee;
      let obj = {
        symbol: symbol,
        optionType: 'longCall',
        size: size,
        tradePrice: entryPremium,
        leverage: 200,
      };
      const PNL = this.calculatePnLOptions(obj, exitPremium);
      this.pnl = PNL - this.totalWithGST;
    }

    // Apply GST once
  };

  calculatePNLFutures = (order: any, currentPrice: any, symbol: any): any => {
    const asset = this.commonService.getFuturesArrayByUnit(symbol)[0];
    if (!asset) {
      console.error('Symbol not found in futuresEpsilonArray');
      return null;
    }

    const lotSize = asset.lotSize; // Lot size from futuresEpsilonArray
    const tradePrice = parseFloat(order.tradePrice); // Entry price
    const size = parseFloat(order.size); // Lot size quantity

    let pnl = 0;

    if (order.positionSide === 'sell') {
      // Short position PNL formula
      pnl = (tradePrice - currentPrice) * size * lotSize;
    } else {
      // Long position PNL formula
      pnl = (currentPrice - tradePrice) * size * lotSize;
    }

    return parseFloat(pnl.toFixed(2)); // Round to 2 decimal places
  };

  calculatePnLOptions = (order: any, currentPrice: any): any => {
    const { symbol, optionType, size, tradePrice, leverage } = order;
    // Extract strike price from the symbol (e.g., C-BTC-98800-060225 -> 98800)
    const strikePrice = parseFloat(symbol.split('-')[2]);

    // Calculate the PnL based on the option type
    let pnl = 0;
    let premiumPaid = parseFloat(tradePrice) * (0.001 * Number(size)); // Total premium paid or received

    switch (optionType) {
      case 'longCall':
        // Long Call Option PnL: max(Spot Price - Strike Price, 0) - Premium Paid
        pnl = (currentPrice - tradePrice) * (0.001 * Number(size));
        break;

      case 'longPut':
        // Long Put Option PnL: max(Strike Price - Spot Price, 0) - Premium Paid
        pnl = (currentPrice - tradePrice) * (0.001 * Number(size));
        break;

      case 'shortCall':
        // Short Call Option PnL: min(Strike Price - Spot Price, 0) + Premium Received
        pnl = (tradePrice - currentPrice) * (0.001 * Number(size));
        break;

      case 'shortPut':
        // Short Put Option PnL: min(Spot Price - Strike Price, 0) + Premium Received
        pnl = (tradePrice - currentPrice) * (0.001 * Number(size));
        break;

      default:
        throw new Error('Unsupported option type');
    }
    return pnl.toFixed(2);
  };

  setMainTab(id: string): void {
    this.activeMainTab = id;
  }

  onContractChange(): void {
    const c = this.selectedContractObj;
    if (c) {
      this.entryPrice = c.basePrice;
      this.exitPrice = Math.round(c.basePrice * 1.012);
    }
    let obj = {
      markPrice: this.entryPrice,
      exitPrice: this.exitPrice,
      contractType: this.calcType,
      size: this.quantity,
      symbol: this.selectedContract,
      indexPrice: this.indexPrice,
    };
    this.calculateTradingFee(obj);
  }

  recalculate(): void {
    let obj = {
      markPrice: this.entryPrice,
      exitPrice: this.exitPrice,
      contractType: this.calcType,
      size: this.quantity,
      symbol: this.selectedContract,
      indexPrice: this.indexPrice,
    };
    this.calculateTradingFee(obj);
  }

  resetCalculator(): void {
    this.calcType = 'futures';
    this.selectedContract = 'btcusd';
    this.entryPrice = 8500000;
    this.exitPrice = 8600000;
    this.quantity = 1;
    this.entryType = 'maker';
    this.exitType = 'taker';
    this.scalperActive = false;
    this.recalculate();
  }
  onContractToggle(symbol: string): void {
    this.selectedContract = symbol;
    this.onContractChange(); // reuse existing logic
  }

  onNavigate(url: string) {
    this.router.navigate([url]);
  }
}
